import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Loader2, CheckCircle2, Pencil, History, ImageOff, AlertTriangle, User as UserIcon, Trash2,
} from "lucide-react";

const VRSTE = ["jelen navadni", "srna", "divji prašič", "gams", "muflon", "damjak", "drugo/neznano"];
const RAZREDI = ["mladič", "lanščak", "odrasla", "starejša odrasla", "stara"];
const PAGE_SIZE = 20;

const selectCls = "w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sl-SI", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AiPhoto = ({ analysisId, idx, authHeaders }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    axios.get(`${API}/ai-jaw/photo/${analysisId}/${idx}`, { headers: authHeaders, responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, idx]);

  if (err) return null;
  if (!url) return <div className="h-20 w-20 rounded-lg bg-slate-100 animate-pulse flex-shrink-0" />;
  return (
    <img
      src={url}
      alt="čeljust"
      className="h-20 w-20 object-cover rounded-lg border border-slate-200 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={() => window.open(url, "_blank")}
      data-testid={`history-photo-${analysisId}-${idx}`}
    />
  );
};

const HistoryConfirmForm = ({ item, authHeaders, onSaved, onCancel }) => {
  const ai = item.ai_result || {};
  const existing = item.confirmed_by_user;
  const aiMid = ((Number(ai.starost_let_min) || 0) + (Number(ai.starost_let_max) || 0)) / 2;
  const initAge = existing
    ? ((Number(existing.starost_let_min) || 0) + (Number(existing.starost_let_max) || 0)) / 2
    : aiMid;
  const initVrsta = existing?.vrsta || ai.vrsta;
  const initRazred = existing?.starostni_razred || ai.starostni_razred;

  const [vrsta, setVrsta] = useState(VRSTE.includes(initVrsta) ? initVrsta : "drugo/neznano");
  const [razred, setRazred] = useState(RAZREDI.includes(initRazred) ? initRazred : "odrasla");
  const [starost, setStarost] = useState(initAge ? String(initAge) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const age = parseFloat(String(starost).replace(",", "."));
    if (isNaN(age) || age < 0 || age > 30) {
      toast.error("Vnesite veljavno starost (0–30 let)");
      return;
    }
    setSaving(true);
    try {
      const confirmed = { vrsta, starostni_razred: razred, starost_let_min: age, starost_let_max: age };
      await axios.post(`${API}/ai-jaw/confirm`, { analysis_id: item.id, confirmed }, { headers: authHeaders });
      toast.success("Ocena komisije shranjena — AI se uči");
      onSaved(confirmed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid={`history-confirm-form-${item.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-[11px] font-medium text-slate-600 block mb-1">Vrsta</label>
          <select value={vrsta} onChange={(e) => setVrsta(e.target.value)} className={selectCls}>
            {VRSTE.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-600 block mb-1">Starost (let) — komisija</label>
          <Input type="number" step="0.5" min="0" max="30" value={starost} onChange={(e) => setStarost(e.target.value)} className="h-9 bg-white" data-testid={`history-starost-input-${item.id}`} />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-600 block mb-1">Starostni razred</label>
          <select value={razred} onChange={(e) => setRazred(e.target.value)} className={selectCls}>
            {RAZREDI.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 flex-1" data-testid={`history-save-btn-${item.id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" />Shrani</>}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Prekliči</Button>
      </div>
    </div>
  );
};

const HistoryItem = ({ item, authHeaders, onUpdated, canDelete, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const ai = item.ai_result;
  const conf = item.confirmed_by_user;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/ai-jaw/analyses/${item.id}`, { headers: authHeaders });
      toast.success("Analiza izbrisana");
      onDeleted(item.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri brisanju");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Card data-testid={`history-item-${item.id}`}>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-medium text-slate-700">{fmtDate(item.created_at)}</span>
          {item.user_name && (
            <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" />{item.user_name}</span>
          )}
          {item.status === "processing" && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">V obdelavi…</Badge>}
          {item.status === "error" && <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">Napaka</Badge>}
          {canDelete && (
            <span className="ml-auto flex items-center gap-1">
              {confirmDelete ? (
                <>
                  <Button size="sm" variant="destructive" className="h-6 px-2 text-[11px]" onClick={doDelete} disabled={deleting} data-testid={`history-delete-confirm-${item.id}`}>
                    {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Potrdi brisanje"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setConfirmDelete(false)}>Prekliči</Button>
                </>
              ) : (
                <button
                  className="text-slate-400 hover:text-red-600 transition-colors p-1"
                  onClick={() => setConfirmDelete(true)}
                  title="Izbriši analizo (samo vodstvo)"
                  data-testid={`history-delete-btn-${item.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </span>
          )}
        </div>

        {item.n_photos > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {Array.from({ length: item.n_photos }).map((_, i) => (
              <AiPhoto key={i} analysisId={item.id} idx={i} authHeaders={authHeaders} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 italic">
            <ImageOff className="h-3.5 w-3.5" />Fotografije niso shranjene (starejša analiza)
          </div>
        )}

        {ai && (
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-[10px] text-slate-500 uppercase">AI ocena</p>
              <p className="text-sm font-semibold text-slate-800">
                {ai.vrsta || "—"}{(ai.starost_let_min || ai.starost_let_max) ? `, ${ai.starost_let_min}–${ai.starost_let_max} let` : ""}
              </p>
              <p className="text-[11px] text-slate-500">{ai.starostni_razred}{ai.gotovost != null ? ` · gotovost ${Math.round(ai.gotovost * 100)}%` : ""}</p>
            </div>
            <div className={`rounded-lg p-2 ${conf ? "bg-emerald-50 border border-emerald-100" : "bg-amber-50 border border-amber-100"}`}>
              <p className="text-[10px] text-slate-500 uppercase">Ocena komisije</p>
              {conf ? (
                <>
                  <p className="text-sm font-semibold text-emerald-800" data-testid={`history-commission-${item.id}`}>
                    {conf.vrsta || "—"}{(conf.starost_let_min || conf.starost_let_max) ? `, ${conf.starost_let_min === conf.starost_let_max ? conf.starost_let_min : `${conf.starost_let_min}–${conf.starost_let_max}`} let` : ""}
                  </p>
                  <p className="text-[11px] text-slate-500">{conf.starostni_razred || ""}</p>
                </>
              ) : (
                <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Še ni vnesena</p>
              )}
            </div>
          </div>
        )}

        {ai && !editing && (
          <Button
            size="sm"
            variant={conf ? "outline" : "default"}
            className={conf ? "w-full" : "w-full bg-emerald-700 hover:bg-emerald-800"}
            onClick={() => setEditing(true)}
            data-testid={`history-edit-btn-${item.id}`}
          >
            {conf ? <><Pencil className="h-3.5 w-3.5 mr-1.5" />Popravi oceno komisije</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" />Vnesi oceno komisije (AI se uči)</>}
          </Button>
        )}
        {ai && editing && (
          <HistoryConfirmForm
            item={item}
            authHeaders={authHeaders}
            onCancel={() => setEditing(false)}
            onSaved={(confirmed) => {
              setEditing(false);
              onUpdated(item.id, confirmed);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export const AIJawHistory = ({ authHeaders, onStatsRefresh, canDelete = false }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback((skip = 0, append = false) => {
    (append ? setLoadingMore : setLoading)(true);
    axios.get(`${API}/ai-jaw/history?skip=${skip}&limit=${PAGE_SIZE}`, { headers: authHeaders })
      .then((r) => {
        setTotal(r.data.total || 0);
        setItems((prev) => (append ? [...prev, ...(r.data.items || [])] : (r.data.items || [])));
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Napaka pri nalaganju zgodovine"))
      .finally(() => { setLoading(false); setLoadingMore(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(0); }, [load]);

  const onUpdated = (id, confirmed) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, confirmed_by_user: confirmed } : it)));
    if (onStatsRefresh) onStatsRefresh();
  };

  const onDeleted = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    if (onStatsRefresh) onStatsRefresh();
  };

  const unconfirmed = items.filter((i) => i.ai_result && !i.confirmed_by_user).length;

  return (
    <div className="space-y-3" data-testid="ai-jaw-history">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-slate-600" />
        <h3 className="text-sm font-semibold text-slate-800">Zgodovina analiz</h3>
        <span className="text-xs text-slate-500 ml-auto">{total} analiz</span>
      </div>
      {unconfirmed > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span><strong>{unconfirmed}</strong> {unconfirmed === 1 ? "analiza še nima" : "analiz še nima"} ocene komisije. Vnesite jih — vsaka ocena izboljša natančnost AI za vašo LD.</span>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-slate-500">Ni še nobene analize. Opravite prvo analizo v zavihku "Nova analiza".</CardContent></Card>
      ) : (
        <>
          {items.map((item) => (
            <HistoryItem key={item.id} item={item} authHeaders={authHeaders} onUpdated={onUpdated} canDelete={canDelete} onDeleted={onDeleted} />
          ))}
          {items.length < total && (
            <Button variant="outline" className="w-full" onClick={() => load(items.length, true)} disabled={loadingMore} data-testid="history-load-more">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : `Naloži več (${items.length}/${total})`}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
